/**
 * The questions somebody sent us, what we have answered, and WHERE EACH ANSWER CAME FROM.
 *
 * AN ANSWER'S SOURCE IS NOT A DETAIL, IT IS THE PHASE. Every answer here is a statement this
 * company makes to a named customer about its own quality system, and the three places one can
 * come from are not interchangeable: the curated library (somebody is accountable for the
 * wording), this company's own documents (with citations, and no citation means no answer), or a
 * person typing. There is no fourth -- in particular no "the model knew this", which is what the
 * source product does, answering from training data and storing it with a confidence of 50. So the
 * source travels beside every answer on this pane and is never collapsed into a badge that says
 * "automatic".
 *
 * AND THE MODEL THAT WROTE IT IS SHOWN, which is why this pane needed a backend fix before it
 * could exist. `ANSWERED_BY_MODEL` is written on every documents answer and sharing REFUSES a
 * response carrying any `ollama:` one, with no flag to override -- but the detail read dropped the
 * name, so a person could not see which answers would stop them sharing until they tried. Now the
 * name travels, and a locally-generated answer is marked here rather than discovered at the door.
 *
 * THE OUTSTANDING QUESTIONS ARE NAMED, NOT COUNTED, and that is the half of this phase a reader
 * actually sees. An answer with no source is not produced, so gaps are the ORDINARY outcome --
 * "eleven unanswered" is a number somebody has to go and investigate, while a list of the eleven
 * is a worklist they can pick up. The service names them for exactly that reason and this shows
 * them first.
 *
 * ONE DRAFT AT A TIME, which the service enforces and this reflects: a second start is refused and
 * names the one that is open, because two people each believing they started the pass is how two
 * half-answered documents happen with no way to tell which is being sent.
 *
 * A SHARED RESPONSE IS READ-ONLY, and `IsReadOnly` comes from the server rather than being
 * re-derived from the state name here.
 *
 * NOTHING ON THIS PANE ANSWERS FROM A MODEL BY ITSELF. The answering is a deliberate POST that
 * names its evidence, because the evidence pack is the caller's and a model that could choose
 * which records to read would be one prompt away from reading records the asker cannot see.
 */
import React, {useCallback, useContext, useEffect, useState} from "react";
import {
    Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    TextField, Tooltip, Typography,
} from "@mui/material";

import {AppContext} from "../../util/AppContext";
import {makeCresAxios} from "../../util/Axios";
import {Blue3, Grey2, Grey5, Grey7, Grey8, Grey17} from "../../theme/styles";

const EDIT_PRIVILEGE = "questionnaire:edit";
const SHARE_PRIVILEGE = "questionnaire:shareResponse";

type Question = {
    USID: string; Ordinal?: number; QuestionReference?: string | null;
    QuestionText?: string | null; Section?: string | null;
    AnsweredInCurrentDraft?: boolean;
};

type Answer = {
    USID: string;
    QuestionnaireQuestionUSID?: string;
    Ordinal?: number;
    QuestionReference?: string | null;
    QuestionText?: string | null;
    AnswerText?: string | null;
    SourceType?: string | null;
    MatchScore?: number | null;
    Citations?: any;
    AnsweredByModel?: string | null;
    AnsweredDatetime?: string | null;
};

type Response_ = {
    USID: string; Title?: string | null; State?: string | null; Notes?: string | null;
    SharedDatetime?: string | null; IsReadOnly?: boolean; CreatedDatetime?: string | null;
    ListOfAnswer?: {Answer?: Answer[] | Answer};
};

type Questionnaire = {
    ObjectID?: string | null; Name?: string | null; Direction?: string | null;
    Description?: string | null; SubmittedByName?: string | null;
    OrganizationName?: string | null; WorkflowState?: string | null;
    DueDate?: string | null; IsClosed?: boolean;
    QuestionCount?: number; AnsweredInCurrentDraft?: number; Outstanding?: number;
};

function listOf<T>(held: T[] | T | undefined | null): T[] {
    if (held === null || held === undefined) return [];
    return Array.isArray(held) ? held : [held];
}

function when(value?: string | null): string {
    if (!value) return "—";
    const at = new Date(String(value).replace(" ", "T"));
    if (Number.isNaN(at.getTime())) return String(value).slice(0, 10);
    return at.toLocaleDateString(undefined, {day: "numeric", month: "short", year: "numeric"});
}

function spell(value?: string | null): string {
    const text = String(value ?? "").replace(/_/g, " ").toLowerCase();
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : "—";
}

/**
 * How an answer describes its own origin.
 *
 * THREE SOURCES, THREE SENTENCES, never one word. "Automatic" would merge the library -- which a
 * person wrote and is accountable for -- with a model reading a PDF, and those carry different
 * weight for whoever signs the document off.
 */
function source(answer: Answer): {label: string; colour: string; detail?: string} {
    const kind = String(answer.SourceType ?? "").toUpperCase();
    if (kind === "LIBRARY") {
        return {label: "from the library", colour: "#1E7B34",
                detail: answer.MatchScore != null
                        ? `matched the curated answer at ${answer.MatchScore}% word overlap`
                        : undefined};
    }
    if (kind === "DOCUMENTS") {
        const cites = Array.isArray(answer.Citations) ? answer.Citations.length : 0;
        return {label: "from our documents", colour: "#1F5FA9",
                detail: cites ? `${cites} citation${cites === 1 ? "" : "s"}` : undefined};
    }
    // MANUAL is the stored constant. A screen keying on "PERSON" -- which is what the prose calls
    // it -- would render every typed answer as having no source at all.
    if (kind === "MANUAL") return {label: "typed by a person", colour: Grey7};
    return {label: spell(kind), colour: Grey7};
}

/** `ollama:` means the prose came off a developer machine and can never go to a customer. */
function isLocalModel(answer: Answer): boolean {
    return String(answer.AnsweredByModel ?? "").startsWith("ollama:");
}

export default function QuestionnairePane(
    {objectUSID, revisionUSID}:
    {objectUSID?: string | null; revisionUSID?: string | null}) {

    const appContext: any = useContext(AppContext);
    const api = makeCresAxios(appContext?.sessionContext);

    const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(null);
    const [questions, setQuestions] = useState<Question[]>([]);
    const [outstanding, setOutstanding] = useState<Question[]>([]);
    const [responses, setResponses] = useState<Response_[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [granted, setGranted] = useState<string[]>([]);
    const [typing, setTyping] = useState<{question: Question; response: Response_;
                                          existing?: Answer} | null>(null);
    const [sharing, setSharing] = useState<Response_ | null>(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(() => {
        if (!objectUSID) return;
        setLoading(true);
        api.get("t1/v1/questionnaire_details", {params: {QuestionnaireObjectUSID: objectUSID}})
            .then((response: any) => {
                const message = response?.data?.PlatformMessage ?? {};
                setQuestionnaire(message?.Questionnaire ?? null);
                setQuestions(listOf<Question>(message?.ListOfQuestion?.Question));
                setOutstanding(listOf<Question>(
                    message?.ListOfOutstandingQuestion?.OutstandingQuestion));
                setResponses(listOf<Response_>(message?.ListOfResponse?.Response));
                setError(null);
            })
            .catch((problem: any) => {
                setQuestionnaire(null);
                setError(problem?.response?.data?.Cause
                         ?? "This questionnaire could not be read.");
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [objectUSID]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (!revisionUSID) return;
        let cancelled = false;
        api.get("t1/v1/object_access", {params: {USID: revisionUSID}})
            .then((response: any) => {
                if (!cancelled) setGranted(response?.data?.EffectivePrivileges ?? []);
            })
            .catch(() => { if (!cancelled) setGranted([]); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [revisionUSID]);

    const startDraft = async () => {
        setBusy(true);
        try {
            await api.post("t1/v1/questionnaire_responses",
                           {QuestionnaireObjectUSID: objectUSID, Title: "First pass"});
            load();
        } catch (problem: any) {
            // ONE DRAFT AT A TIME, and the refusal NAMES the one that is open rather than quietly
            // returning it -- so this shows the sentence rather than appearing to do nothing.
            setError(problem?.response?.data?.Cause ?? "A draft could not be started.");
        } finally {
            setBusy(false);
        }
    };

    if (loading && !questionnaire && !error) {
        return <Box sx={{display: "flex", gap: "10px", alignItems: "center", padding: "20px 0"}}>
            <CircularProgress size={18}/>
            <Typography sx={{fontSize: 13, color: Grey7}}>Reading the questionnaire…</Typography>
        </Box>;
    }

    if (error && !questionnaire) {
        return <Typography sx={{fontSize: 13, color: "#B3261E", lineHeight: 1.6, maxWidth: 700,
                                paddingTop: "8px"}}>{error}</Typography>;
    }

    if (!questionnaire) return null;

    const mayEdit = granted.includes(EDIT_PRIVILEGE);
    const mayShare = granted.includes(SHARE_PRIVILEGE);
    const draft = responses.find(one => String(one.State).toUpperCase() === "DRAFT") ?? null;
    const draftAnswers = listOf<Answer>(draft?.ListOfAnswer?.Answer);
    const answerFor = new Map(draftAnswers.map(one => [one.QuestionnaireQuestionUSID, one]));
    const localAnswers = draftAnswers.filter(isLocalModel);

    return (
        <Box sx={{paddingTop: "6px", maxWidth: 1020}}>

            {/* ── who asked, and what is outstanding ────────────────────────────────────────── */}
            <Box sx={{display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap",
                      marginBottom: "10px"}}>
                <Chip size="small" label={spell(questionnaire.WorkflowState)}
                      sx={{height: 22, fontSize: 12}}/>
                <Typography sx={{fontSize: 13, color: Grey7}}>
                    {questionnaire.Direction === "INBOUND_FROM_CUSTOMER"
                        ? `From ${questionnaire.OrganizationName || "a customer"}`
                        : `To ${questionnaire.OrganizationName || "a supplier"}`}
                    {questionnaire.SubmittedByName ? ` — ${questionnaire.SubmittedByName}` : ""}
                </Typography>
                <Box sx={{flex: 1}}/>
                {questionnaire.DueDate && (
                    <Typography sx={{fontSize: 13, color: Grey5}}>
                        due {when(questionnaire.DueDate)}
                    </Typography>
                )}
            </Box>

            <Box sx={{display: "flex", gap: "28px", flexWrap: "wrap",
                      border: `1px solid ${Grey2}`, borderRadius: "6px",
                      padding: "12px 16px", marginBottom: "18px"}}>
                <Fact label="Questions" value={questionnaire.QuestionCount ?? questions.length}/>
                <Fact label="Answered in this draft"
                      value={questionnaire.AnsweredInCurrentDraft ?? draftAnswers.length}/>
                <Fact label="Outstanding" value={outstanding.length}/>
            </Box>

            {error && (
                <Typography sx={{fontSize: 13, color: "#B3261E", marginBottom: "12px",
                                 lineHeight: 1.6}}>
                    {error}
                </Typography>
            )}

            {/* AN ANSWER OFF A DEVELOPER MACHINE CAN NEVER BE SENT, and until this pane the only
                way to find that out was to try to share and be refused. Named here, with the
                count, before anybody gets that far. */}
            {!!localAnswers.length && (
                <Box sx={{border: `1px solid #F0D8A8`, background: "#FDF8EE", borderRadius: "6px",
                          padding: "12px 14px", marginBottom: "18px"}}>
                    <Typography sx={{fontSize: 13, color: "#8A5A00", lineHeight: 1.6,
                                     maxWidth: 820}}>
                        {localAnswers.length === 1
                            ? "One answer in this draft was written by a model running on a "
                              + "developer machine"
                            : `${localAnswers.length} answers in this draft were written by a model `
                              + "running on a developer machine"}
                        {" "}— so this response cannot be shared with the counterparty. An answer
                        here is a statement this company makes about its own quality system, and
                        real citations beside it do not change what wrote the prose. Clear them and
                        re-run the answering against the production model.
                    </Typography>
                </Box>
            )}

            {/* ── the gaps, first, because they are the deliverable ─────────────────────────── */}
            <Typography sx={{fontSize: 14, fontWeight: 600, color: Grey8, marginBottom: "8px"}}>
                Still unanswered
            </Typography>
            {outstanding.length === 0 ? (
                <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6, maxWidth: 820,
                                 marginBottom: "18px"}}>
                    Every question has an answer in the current draft.
                </Typography>
            ) : (
                <Box sx={{marginBottom: "18px"}}>
                    {/* NAMED, NOT COUNTED. "Eleven unanswered" is a number somebody has to go and
                        investigate; this is a worklist they can pick up. */}
                    {outstanding.map(one => (
                        <Box key={one.USID}
                             sx={{display: "flex", gap: "10px", alignItems: "baseline",
                                  padding: "6px 0", borderBottom: `1px solid ${Grey2}`}}>
                            <Typography sx={{fontSize: 12, color: Grey5, minWidth: 34}}>
                                {one.QuestionReference || `#${(one.Ordinal ?? 0) + 1}`}
                            </Typography>
                            <Typography sx={{fontSize: 13, color: Grey17, lineHeight: 1.6,
                                             flex: 1}}>
                                {one.QuestionText}
                            </Typography>
                            {mayEdit && draft && (
                                <Button size="small"
                                        onClick={() => setTyping({question: one, response: draft})}>
                                    Answer
                                </Button>
                            )}
                        </Box>
                    ))}
                    <Typography sx={{fontSize: 12, color: Grey5, lineHeight: 1.6, marginTop: "8px",
                                     maxWidth: 820}}>
                        Gaps are the ordinary outcome, not a fault: an answer with no source is
                        never produced, so a question nothing in the library or the documents
                        covers stays here until somebody writes one.
                    </Typography>
                </Box>
            )}

            {/* ── the draft, and every answer's source ──────────────────────────────────────── */}
            <Box sx={{display: "flex", alignItems: "center", gap: "12px", marginBottom: "10px"}}>
                <Typography sx={{fontSize: 14, fontWeight: 600, color: Grey8}}>
                    {draft ? `Draft — ${draft.Title || "untitled"}` : "Draft"}
                </Typography>
                <Box sx={{flex: 1}}/>
                {!draft && mayEdit && !questionnaire.IsClosed && (
                    <Button size="small" variant="outlined" disabled={busy} onClick={startDraft}>
                        Start a draft
                    </Button>
                )}
                {draft && mayShare && (
                    <Button size="small" variant="contained"
                            onClick={() => setSharing(draft)}>
                        Share with the counterparty
                    </Button>
                )}
            </Box>

            {!draft ? (
                <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6, maxWidth: 820}}>
                    No pass has been started. A questionnaire is answered in a draft response, one
                    at a time — a second one while the first is open is refused, because two people
                    each believing they started the pass is how two half-answered documents get
                    sent.
                </Typography>
            ) : draftAnswers.length === 0 ? (
                <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6, maxWidth: 820}}>
                    Nothing is answered yet in this draft.
                </Typography>
            ) : (
                <Box sx={{border: `1px solid ${Grey2}`, borderRadius: "6px", overflow: "hidden"}}>
                    {questions.filter(one => answerFor.has(one.USID)).map(question => {
                        const answer = answerFor.get(question.USID)!;
                        const origin = source(answer);
                        return (
                            <Box key={question.USID}
                                 sx={{padding: "12px 14px", borderBottom: `1px solid ${Grey2}`,
                                      "&:last-of-type": {borderBottom: "none"}}}>
                                <Box sx={{display: "flex", gap: "10px", alignItems: "baseline"}}>
                                    <Typography sx={{fontSize: 12, color: Grey5, minWidth: 34}}>
                                        {question.QuestionReference
                                         || `#${(question.Ordinal ?? 0) + 1}`}
                                    </Typography>
                                    <Typography sx={{fontSize: 13, color: Grey8, fontWeight: 600,
                                                     flex: 1, lineHeight: 1.6}}>
                                        {question.QuestionText}
                                    </Typography>
                                    {mayEdit && !draft.IsReadOnly && (
                                        <Button size="small"
                                                onClick={() => setTyping({question,
                                                                          response: draft,
                                                                          existing: answer})}>
                                            Rewrite
                                        </Button>
                                    )}
                                </Box>
                                <Typography sx={{fontSize: 13, color: Grey17, lineHeight: 1.7,
                                                 marginTop: "6px", marginLeft: "44px",
                                                 whiteSpace: "pre-wrap"}}>
                                    {answer.AnswerText}
                                </Typography>
                                <Box sx={{display: "flex", gap: "10px", alignItems: "center",
                                          marginTop: "6px", marginLeft: "44px",
                                          flexWrap: "wrap"}}>
                                    {/* THE SOURCE, ALWAYS, AND NEVER AS ONE WORD. */}
                                    <Typography sx={{fontSize: 12, color: origin.colour}}>
                                        {origin.label}
                                    </Typography>
                                    {origin.detail && (
                                        <Typography sx={{fontSize: 12, color: Grey5}}>
                                            {origin.detail}
                                        </Typography>
                                    )}
                                    {answer.AnsweredByModel && (
                                        <Tooltip title={isLocalModel(answer)
                                            ? "Written on a developer machine. This response "
                                              + "cannot be shared while it is here."
                                            : "Written by the production model."}>
                                            <Typography sx={{fontSize: 12,
                                                             color: isLocalModel(answer)
                                                                    ? "#B26A00" : Grey5}}>
                                                {answer.AnsweredByModel}
                                            </Typography>
                                        </Tooltip>
                                    )}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            )}

            {/* ── what has already gone out ─────────────────────────────────────────────────── */}
            {responses.some(one => String(one.State).toUpperCase() === "SHARED") && (
                <>
                    <Typography sx={{fontSize: 14, fontWeight: 600, color: Grey8,
                                     marginTop: "20px", marginBottom: "8px"}}>
                        Already shared
                    </Typography>
                    {responses.filter(one => String(one.State).toUpperCase() === "SHARED")
                        .map(one => (
                            <Box key={one.USID}
                                 sx={{border: `1px solid ${Grey2}`, borderRadius: "6px",
                                      padding: "10px 14px", marginBottom: "8px",
                                      display: "flex", gap: "12px", alignItems: "center"}}>
                                <Typography sx={{fontSize: 13, color: Grey17}}>
                                    {one.Title || "untitled"}
                                </Typography>
                                <Box sx={{flex: 1}}/>
                                <Typography sx={{fontSize: 12, color: Grey5}}>
                                    sent {when(one.SharedDatetime)}
                                </Typography>
                            </Box>
                        ))}
                </>
            )}

            {typing && (
                <TypeAnswerDialog subject={typing}
                                  onClose={() => setTyping(null)}
                                  onSaved={() => { setTyping(null); load(); }}/>
            )}

            {sharing && (
                <ShareDialog response={sharing}
                             outstanding={outstanding.length}
                             onClose={() => setSharing(null)}
                             onShared={() => { setSharing(null); load(); }}/>
            )}
        </Box>
    );
}

function Fact({label, value}: {label: string; value: React.ReactNode}) {
    return (
        <Box>
            <Typography sx={{fontSize: 11, color: Grey5, textTransform: "uppercase",
                             letterSpacing: "0.04em"}}>
                {label}
            </Typography>
            <Typography sx={{fontSize: 15, color: Grey17, marginTop: "2px", fontWeight: 600}}>
                {value}
            </Typography>
        </Box>
    );
}

/**
 * A person writes one.
 *
 * TYPING OVER A SOURCED ANSWER REPLACES ITS SOURCE, and the dialog says so. An answer that read
 * "from the library" while carrying somebody's edits would misattribute the wording -- the library
 * entry is what somebody is accountable for, and this is no longer it.
 */
function TypeAnswerDialog({subject, onClose, onSaved}: {
    subject: {question: Question; response: Response_; existing?: Answer};
    onClose: () => void; onSaved: () => void;
}) {
    const appContext: any = useContext(AppContext);
    const api = makeCresAxios(appContext?.sessionContext);

    const [text, setText] = useState(subject.existing?.AnswerText ?? "");
    const [saving, setSaving] = useState(false);
    const [problem, setProblem] = useState<string | null>(null);

    const save = async () => {
        setSaving(true);
        setProblem(null);
        try {
            await api.put("t1/v1/questionnaire_answers", {
                QuestionnaireResponseUSID: subject.response.USID,
                QuestionnaireQuestionUSID: subject.question.USID,
                AnswerText: text,
            });
            onSaved();
        } catch (caught: any) {
            setProblem(caught?.response?.data?.Cause ?? "That answer could not be saved.");
            setSaving(false);
        }
    };

    const replacing = !!subject.existing
                      && String(subject.existing.SourceType ?? "").toUpperCase() !== "MANUAL";

    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{fontSize: 16}}>
                {subject.existing ? "Rewrite this answer" : "Answer this question"}
            </DialogTitle>
            <DialogContent>
                <Box sx={{display: "flex", flexDirection: "column", gap: "14px",
                          paddingTop: "4px"}}>
                    <Typography sx={{fontSize: 13, color: Grey8, lineHeight: 1.6,
                                     fontWeight: 600}}>
                        {subject.question.QuestionText}
                    </Typography>
                    <TextField label="Answer" size="small" fullWidth multiline minRows={4}
                               value={text}
                               onChange={event => setText(event.target.value)}/>
                    {replacing && (
                        <Typography sx={{fontSize: 12, color: "#B26A00", lineHeight: 1.6}}>
                            This answer currently says it came{" "}
                            {source(subject.existing!).label}. Saving your own wording replaces
                            that: it will be recorded as typed by a person, because it no longer is
                            what the source said.
                        </Typography>
                    )}
                    {problem && (
                        <Typography sx={{fontSize: 13, color: "#B3261E", lineHeight: 1.6}}>
                            {problem}
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button size="small" onClick={onClose}>Cancel</Button>
                <Button size="small" variant="contained" disabled={!text.trim() || saving}
                        onClick={save}>
                    {saving ? "Saving…" : "Save"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/**
 * Sending it.
 *
 * SHARING WITH GAPS IS A JUDGEMENT SOMEBODY CAN MAKE, so `AllowUnanswered` is offered -- and it is
 * a tick the person has to make rather than a default, because the ordinary case is that gaps mean
 * the document is not finished.
 *
 * SHARING A LOCALLY-GENERATED ANSWER IS NOT A JUDGEMENT ANYBODY CAN MAKE. There is no flag for it
 * here because there is none in the service, deliberately: an answer written by whatever model a
 * developer had running is not a statement this company makes, and a flag would be an invitation.
 * The pane has already said so above; the refusal, if it still comes, arrives in full.
 */
function ShareDialog({response, outstanding, onClose, onShared}: {
    response: Response_; outstanding: number; onClose: () => void; onShared: () => void;
}) {
    const appContext: any = useContext(AppContext);
    const api = makeCresAxios(appContext?.sessionContext);

    const [allowGaps, setAllowGaps] = useState(false);
    const [saving, setSaving] = useState(false);
    const [problem, setProblem] = useState<string | null>(null);

    const share = async () => {
        setSaving(true);
        setProblem(null);
        try {
            await api.patch("t1/v1/questionnaire_responses", {
                QuestionnaireResponseUSID: response.USID,
                Share: true,
                AllowUnanswered: allowGaps || undefined,
            });
            onShared();
        } catch (caught: any) {
            setProblem(caught?.response?.data?.Cause ?? "That response could not be shared.");
            setSaving(false);
        }
    };

    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{fontSize: 16}}>Share with the counterparty</DialogTitle>
            <DialogContent>
                <Box sx={{display: "flex", flexDirection: "column", gap: "14px",
                          paddingTop: "4px"}}>
                    <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6}}>
                        This puts the answers in front of the person who sent the questionnaire.
                        Once shared, the response is read-only.
                    </Typography>
                    {outstanding > 0 && (
                        <Box>
                            <Typography sx={{fontSize: 13, color: "#B26A00", lineHeight: 1.6}}>
                                {outstanding === 1
                                    ? "1 question has no answer."
                                    : `${outstanding} questions have no answer.`}
                                {" "}Sending anyway is a deliberate act.
                            </Typography>
                            <Button size="small"
                                    variant={allowGaps ? "contained" : "outlined"}
                                    sx={{marginTop: "8px"}}
                                    onClick={() => setAllowGaps(one => !one)}>
                                {allowGaps ? "Sending with gaps" : "Send with the gaps"}
                            </Button>
                        </Box>
                    )}
                    {problem && (
                        <Typography sx={{fontSize: 13, color: "#B3261E", lineHeight: 1.6}}>
                            {problem}
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button size="small" onClick={onClose}>Cancel</Button>
                <Button size="small" variant="contained"
                        disabled={saving || (outstanding > 0 && !allowGaps)}
                        onClick={share}>
                    {saving ? "Sending…" : "Share"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
